import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Loader2, Copy, CheckCircle2, Clock, XCircle, Ban, Cog, ThumbsUp, AlertTriangle } from 'lucide-react';
import { statusOf, formatINR, fmtDateTime, maskAcct, copyText, formatCopyItem } from './payoutUtils';
import { SettlementDetails } from './SettlementDetails';

const Section = ({ title, children, testId }) => (
  <section data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">{title}</p>
    <div className="grid sm:grid-cols-2 gap-x-6">{children}</div>
  </section>
);
const Row = ({ k, v, mono, testId }) => (
  <div className="flex items-start justify-between gap-3 py-1.5 border-b border-border/40 text-sm" data-testid={testId}>
    <span className="text-muted-foreground shrink-0">{k}</span>
    <span className={`text-right font-medium break-all ${mono ? 'font-mono' : ''} ${v == null ? 'text-muted-foreground font-normal' : ''}`}>{v ?? 'Not available'}</span>
  </div>
);

const STATE = {
  created: { Icon: Clock, tone: 'amber', title: 'Payout is pending review', msg: 'Your payout request has been submitted and is currently waiting for processing.' },
  pending_admin_review: { Icon: Clock, tone: 'amber', title: 'Payout is pending review', msg: 'Your payout request has been submitted and is currently waiting for processing.' },
  approved: { Icon: ThumbsUp, tone: 'blue', title: 'Payout Approved', msg: 'Your payout request has been approved and is awaiting processing.' },
  processing: { Icon: Cog, tone: 'violet', title: 'Payout Processing', msg: 'Your payout is currently being processed.' },
  paid: { Icon: CheckCircle2, tone: 'green', title: 'Payout Completed', msg: 'Your payout has been successfully completed.' },
  rejected: { Icon: XCircle, tone: 'red', title: 'Payout Rejected', msg: 'Your payout request was not approved.' },
  cancelled: { Icon: Ban, tone: 'slate', title: 'Payout Cancelled', msg: 'This payout was cancelled and held funds were released to your wallet.' },
  failed: { Icon: AlertTriangle, tone: 'red', title: 'Payout Failed', msg: 'The bank transfer could not be completed. Our team will retry or release the funds.' },
  reversed: { Icon: AlertTriangle, tone: 'orange', title: 'Payout Reversed', msg: 'The transfer was reversed by the bank and funds were returned to your wallet.' },
};
const TONE = {
  amber: 'border-amber-500/40 bg-amber-500/5 text-amber-700', blue: 'border-blue-500/40 bg-blue-500/5 text-blue-700', violet: 'border-violet-500/40 bg-violet-500/5 text-violet-700',
  green: 'border-green-500/40 bg-green-500/5 text-green-700', red: 'border-red-500/40 bg-red-500/5 text-red-700', slate: 'border-slate-400/40 bg-slate-500/5 text-slate-600', orange: 'border-orange-500/40 bg-orange-500/5 text-orange-700',
};

// Merchant-facing Payout Details modal — all data from GET /payouts/{id}/details (+ settlement endpoint).
export const MerchantPayoutModal = ({ payoutId, open, onOpenChange }) => {
  const { api } = useAuth();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [settlement, setSettlement] = useState(null);

  useEffect(() => {
    if (!open || !payoutId) return;
    setLoading(true); setD(null); setSettlement(null);
    api.get(`/payouts/${payoutId}/details`).then(r => setD(r.data)).catch(e => toast.error(e.response?.data?.detail || 'Failed to load payout')).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, payoutId]);

  const p = d?.payout;
  const b = d?.beneficiary || {};
  const st = p ? statusOf(p.status) : null;
  const state = p ? (STATE[p.status] || STATE.created) : null;
  const masked = maskAcct(p?.account_number_masked || b.account_number_masked);

  const copy = async () => {
    const ok = await copyText(formatCopyItem({ order_id: p.order_id, account_holder_name: p.beneficiary_name, bank_name: p.bank_name, account_number: p.account_number_masked || masked, ifsc_code: p.ifsc_code, amount: p.amount }));
    ok ? toast.success('Copied ✓') : toast.error('Unable to copy. Please try again.');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[680px] w-[calc(100vw-1rem)] sm:w-full max-h-[92vh] overflow-y-auto rounded-lg p-0 gap-0 shadow-2xl" data-testid="merchant-payout-modal">
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-border/60 space-y-1 text-left">
          <DialogTitle className="font-heading text-lg">Payout Details</DialogTitle>
          <DialogDescription asChild>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-sm text-muted-foreground">Order ID: <span className="font-mono font-semibold text-foreground" data-testid="mp-order-id">{p?.order_id || '…'}</span></span>
              {st && <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-sm border ${st.cls}`} data-testid="mp-status-badge"><span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />{p.status === 'pending_admin_review' ? 'Pending Review' : st.label}</span>}
            </div>
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" />Loading…</div>}
        {p && (
          <div className="px-6 py-4 space-y-5">
            <div className={`p-3.5 rounded-sm border flex items-start gap-3 ${TONE[state.tone]}`} data-testid="mp-state-banner">
              <state.Icon className="h-5 w-5 mt-0.5 shrink-0" />
              <div className="text-sm">
                <p className="font-semibold" data-testid="mp-state-title">{state.title}</p>
                <p className="text-xs opacity-90 mt-0.5">{state.msg}</p>
                {p.status === 'paid' && <p className="text-xs mt-1">Completed {fmtDateTime(p.paid_at)}{d.processing?.utr ? ` · UTR ${d.processing.utr}` : ''}</p>}
                {d.approval && ['approved', 'processing'].includes(p.status) && <p className="text-xs mt-1">Approved {fmtDateTime(d.approval.approved_at)} by {d.approval.approved_by}</p>}
                {p.status === 'processing' && d.processing?.processed_at && <p className="text-xs mt-1">Processing started {fmtDateTime(d.processing.processed_at)}{d.processing.transaction_id ? ` · Txn ${d.processing.transaction_id}` : ''}</p>}
                {d.rejection && <p className="text-xs mt-1" data-testid="mp-rejection-reason"><span className="font-medium">Reason:</span> {d.rejection.reason || 'Not available'} · {fmtDateTime(d.rejection.rejected_at)} · {d.rejection.rejected_by}</p>}
                {d.cancellation && <p className="text-xs mt-1" data-testid="mp-cancel-reason"><span className="font-medium">Reason:</span> {d.cancellation.reason || 'Not available'} · {fmtDateTime(d.cancellation.cancelled_at)}</p>}
                {d.failure && <p className="text-xs mt-1"><span className="font-medium">Reason:</span> {d.failure.reason || 'Not available'}</p>}
              </div>
            </div>

            <div className="rounded-sm border border-border/60 p-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3" data-testid="mp-amount-card">
              <div><p className="text-3xl font-heading font-bold font-mono" data-testid="mp-amount">{formatINR(p.amount, 2)}</p><p className="text-xs text-muted-foreground">Payout Amount</p></div>
              <div className="text-xs space-y-1 sm:text-right">
                <p className="flex justify-between gap-6"><span className="text-muted-foreground">Fee</span><span className="font-mono">{formatINR(p.fee, 2)}</span></p>
                <p className="flex justify-between gap-6"><span className="text-muted-foreground">GST</span><span className="font-mono">{formatINR(p.gst, 2)}</span></p>
                <p className="flex justify-between gap-6 font-medium border-t border-border/50 pt-1"><span>Total Debit</span><span className="font-mono" data-testid="mp-total-debit">{formatINR(p.total_debit, 2)}</span></p>
              </div>
            </div>

            <Section title="Order Information" testId="mp-order-info">
              <Row k="Order ID" v={p.order_id} mono /><Row k="Reference" v={p.merchant_reference} mono />
              <Row k="Created" v={fmtDateTime(p.created_at)} /><Row k="Updated" v={fmtDateTime(p.updated_at)} />
              <Row k="Status" v={p.status === 'pending_admin_review' ? 'Pending Review' : st.label} />
            </Section>
            <Section title="Beneficiary Details" testId="mp-beneficiary">
              <Row k="Account Holder" v={p.beneficiary_name || b.beneficiary_name} /><Row k="Bank" v={p.bank_name || b.bank_name} />
              <Row k="Account" v={masked} mono testId="mp-account" /><Row k="IFSC" v={p.ifsc_code || b.ifsc_code} mono />
            </Section>
            <Section title="Transaction Details" testId="mp-transaction">
              <Row k="Payout Amount" v={formatINR(p.amount, 2)} mono /><Row k="Total Debit" v={formatINR(p.total_debit, 2)} mono />
              <Row k="Processing Partner" v={d.processing?.partner} /><Row k="UTR" v={d.processing?.utr} mono testId="mp-utr" />
              <Row k="Transaction ID" v={d.processing?.transaction_id} mono /><Row k="Processing Time" v={d.processing?.processed_at ? fmtDateTime(d.processing.processed_at) : null} />
            </Section>

            <Button variant="outline" className="w-full rounded-sm" onClick={copy} data-testid="mp-copy-btn"><Copy className="h-4 w-4 mr-2" />Copy Details</Button>

            {p.status === 'paid' && (
              settlement?.proofs?.length === 0 && settlement?.settlements?.length === 0 && !settlement?.legacy_utr
                ? <p className="text-sm text-muted-foreground border border-dashed border-border/60 rounded-sm p-3" data-testid="mp-no-proof">Payment proof is not available for this payout.</p>
                : null
            )}
            <SettlementDetails payoutId={p.id} hideWhenEmpty onLoaded={setSettlement} />

            {d.timeline?.length > 0 && (
              <section data-testid="mp-timeline">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Timeline</p>
                <ol className="relative border-l border-border/60 ml-1.5 space-y-2.5">
                  {d.timeline.map((t, i) => (
                    <li key={i} className="ml-4 text-xs" data-testid={`mp-timeline-${t.event.toLowerCase().replace(/\s+/g, '-')}`}>
                      <span className={`absolute -left-[5px] mt-1 w-2.5 h-2.5 rounded-full ${['Rejected', 'Failed', 'Cancelled'].includes(t.event) ? 'bg-red-500' : ['Completed', 'Settlement Verified'].includes(t.event) ? 'bg-green-500' : 'bg-primary'}`} />
                      <p className="font-medium text-sm">{t.event}</p>
                      <p className="text-muted-foreground">{fmtDateTime(t.at)}{t.by ? ` · ${t.by}` : ''}</p>
                      {t.note && <p className="text-muted-foreground">{t.note}</p>}
                    </li>
                  ))}
                </ol>
              </section>
            )}
          </div>
        )}
        <DialogFooter className="px-6 py-3 border-t border-border/60">
          <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="mp-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
